<div class="appearance-workspace">
  <div class="columns-row">
    <!-- Left column -->
    <div class="workspace-col">
      <!-- Theme card -->
      <div class="setting-card">
        <div class="section-title">
          <span class="icon-chip"><i class="bx bx-palette"></i></span>
          <span class="card-title">Theme</span>
        </div>

        <div class="theme-grid">
          @for (row of themeRows; track $index) {
            <div class="theme-row">
              @for (preset of row; track preset.key) {
                <button
                  class="theme-card"
                  [class.selected]="draft.theme === preset.key"
                  (click)="setTheme(preset.key)"
                  [attr.aria-pressed]="draft.theme === preset.key"
                >
                  <span class="swatches">
                    @for (swatch of preset.swatches; track $index) {
                      <span class="swatch" [style.background]="swatch"></span>
                    }
                  </span>
                  <span class="theme-name">{{ preset.label }}</span>
                  @if (draft.theme === preset.key) {
                    <span class="active-badge">
                      <i class="bx bx-check"></i>
                      <span>Active</span>
                    </span>
                  }
                </button>
              }
            </div>
          }
        </div>
      </div>

      <!-- Density & Spacing card -->
      <div class="setting-card">
        <div class="section-title">
          <span class="icon-chip"><i class="bx bx-grid-alt"></i></span>
          <span class="card-title">Density &amp; Spacing</span>
        </div>

        <div class="field">
          <span class="field-label">Interface Density</span>
          <div class="segmented">
            @for (option of densityOptions; track option.value) {
              <button
                class="segment"
                [class.selected]="draft.density === option.value"
                (click)="setDensity(option.value)"
              >
                <i [class]="option.icon"></i>
                <span>{{ option.label }}</span>
              </button>
            }
          </div>
        </div>

        <div class="field">
          <span class="field-label">Table Row Height</span>
          <div class="stepper">
            <button class="step-btn" (click)="stepRowHeight(-1)" aria-label="Decrease row height">
              <i class="bx bx-minus"></i>
            </button>
            <span class="step-value">{{ draft.tableRowHeight }} px</span>
            <button class="step-btn" (click)="stepRowHeight(1)" aria-label="Increase row height">
              <i class="bx bx-plus"></i>
            </button>
          </div>
        </div>

        <div class="field">
          <span class="field-label">Cell Horizontal Padding</span>
          <div class="stepper">
            <button
              class="step-btn"
              (click)="stepCellPadding(-1)"
              aria-label="Decrease cell padding"
            >
              <i class="bx bx-minus"></i>
            </button>
            <span class="step-value">{{ draft.cellPaddingX }} px</span>
            <button
              class="step-btn"
              (click)="stepCellPadding(1)"
              aria-label="Increase cell padding"
            >
              <i class="bx bx-plus"></i>
            </button>
          </div>
        </div>

        <div class="field">
          <span class="field-label">Global Corner Radius</span>
          <div class="segmented radius-segmented">
            @for (option of radiusOptions; track option.value) {
              <button
                class="segment"
                [class.selected]="draft.cornerRadius === option.value"
                (click)="setCornerRadius(option.value)"
              >
                <span>{{ option.label }}</span>
              </button>
            }
          </div>
        </div>

        <p class="field-hint">
          Applies to buttons, cards, inputs, dropdowns, and modals across the application.
        </p>
      </div>
    </div>

    <!-- Right column -->
    <div class="workspace-col">
      <!-- Typography card -->
      <div class="setting-card">
        <div class="section-title">
          <span class="icon-chip"><i class="bx bx-font"></i></span>
          <span class="card-title">Typography</span>
        </div>

        <div class="field">
          <span class="field-label">Font Family</span>
          <div class="font-list">
            @for (family of fontFamilies; track family.key) {
              <button
                class="font-option"
                [class.selected]="draft.fontFamily === family.key"
                (click)="setFontFamily(family.key)"
                [attr.aria-pressed]="draft.fontFamily === family.key"
              >
                <span class="font-name">
                  {{ family.label }}
                  @if (family.key === defaultFontFamily) {
                    <span class="font-default-tag">Default</span>
                  }
                </span>
                <span class="font-sample" [style.fontFamily]="family.css">
                  The quick brown fox · 0123456789
                </span>
              </button>
            }
          </div>
        </div>

        <div class="field">
          <span class="field-label">Font Weight</span>
          <select
            class="field-select"
            [ngModel]="draft.fontWeight"
            (ngModelChange)="setFontWeight($event)"
          >
            @for (option of fontWeightOptions; track option.value) {
              <option [value]="option.value">{{ option.label }}</option>
            }
          </select>
        </div>

        <div class="field slider-field">
          <div class="field-label-row">
            <span class="field-label">Base Font Size</span>
            <span class="field-value">{{ fontSizeValueLabel }}</span>
          </div>
          <div class="slider-wrapper">
            <input
              class="size-slider"
              type="range"
              min="0"
              max="3"
              step="1"
              aria-label="Base font size"
              [style.--fill.%]="sliderFillPercent"
              [ngModel]="fontSizeIndex"
              (ngModelChange)="fontSizeIndex = +$event"
            />
            <div class="tick-marks">
              @for (tick of fontSizeTicks; track tick.value) {
                <span class="tick-mark" [class.tick-mark-on]="$index <= fontSizeIndex"></span>
              }
            </div>
          </div>
          <div class="tick-row">
            @for (tick of fontSizeTicks; track tick.value) {
              <span class="tick" [class.tick-active]="draft.fontSize === tick.value">
                {{ tick.label }}
              </span>
            }
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Actions -->
  <div class="action-footer">
    <button class="reset-btn" [disabled]="isDefault" (click)="reset()">Reset to Defaults</button>
    <button class="save-btn" [disabled]="!isDirty" (click)="save()">Save Changes</button>
  </div>
</div>
